import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateGuestProfile, mergeGuest } from '@/api/generated/sdk.gen';
import type { UpdateGuestProfileRequest, GuestRef } from '@/api/generated/types.gen';
import {
  guestQuery,
  guestBookingsQuery,
  guestConversationsQuery,
  guestsQuery,
  type Guest,
  type GuestBooking,
  type ThreadSummary,
} from '@/lib/guests';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import { ApiError } from '@/lib/api';
import { useConfirm } from '@/components/DialogProvider';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { formatMoney, formatDateTime } from '@/lib/format';
import { BookingStatusBadge } from '@/components/StatusBadges';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GuestContactChannels } from '@/components/GuestContactChannels';

// rawBody is a thin cast helper: @hey-ai/openapi-ts drops the null arm from
// nullable optional fields, but the backend PATCH /guests/{id} accepts
// explicit null to clear a column. Cast through this so the wire payload
// preserves explicit nulls (email, phone, language, notes).
function rawBody<T extends UpdateGuestProfileRequest>(b: Record<string, unknown>): T {
  return b as unknown as T;
}

export const Route = createFileRoute('/_app/guests/$id')({
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(guestQuery(params.id));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 404) return;
      throw err;
    }
  },
  component: GuestDetailPage,
});

function GuestDetailPage() {
  const { id } = Route.useParams();
  const { data, error, isLoading } = useQuery(guestQuery(id));

  // Title lives in the Topbar (navTitle → "Guests"); publish the guest's name
  // as the subtitle (the detail identity), mirroring the design's side-panel
  // title. Falls back to navSubtitle until the guest loads.
  usePageHeader({ subtitle: data?.full_name });

  const is401 = error instanceof ApiError && error.status === 401;
  const is404 = error instanceof ApiError && error.status === 404;
  const errorMessage = is401
    ? 'Your session has expired.'
    : is404
      ? 'Guest not found.'
      : error instanceof ApiError
        ? error.message
        : error
          ? String(error)
          : null;

  return (
    <div className="max-w-5xl">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted">
        <Link to="/guests" className="hover:underline">Guests</Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">{data?.full_name ?? id.slice(0, 8)}</span>
      </nav>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
          {is401 && (
            <>
              {' '}
              <Link to="/login" className="font-medium underline">
                Sign in again
              </Link>
              .
            </>
          )}
        </div>
      )}

      {data && <GuestDetail guest={data} />}
    </div>
  );
}

export function GuestDetail({ guest }: { guest: Guest }) {
  const mergedInto = guest.merged_into;
  const isTombstone = mergedInto != null;
  return (
    <>
      {mergedInto && (
        <div
          role="alert"
          className="mb-6 rounded-[var(--radius-control)] border border-[var(--ollio-warning)] bg-[var(--ollio-warning-bg)] px-4 py-3 text-sm"
        >
          Merged into{' '}
          <Link
            to="/guests/$id"
            params={{ id: mergedInto.id }}
            className="font-medium underline"
          >
            {mergedInto.name} →
          </Link>
        </div>
      )}
      <ProfileSection guest={guest} isTombstone={isTombstone} />
      <GuestStatsSection guest={guest} />
      {guest.merged_from && guest.merged_from.length > 0 && (
        <MergedFromSection mergedFrom={guest.merged_from} />
      )}
      <GuestContactChannels guestId={guest.id} />
      <BookingsPanel guestId={guest.id} />
      <ConversationsPanel guestId={guest.id} />
      {!isTombstone && <MergePanel guest={guest} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview: aggregates (bookings count + per-currency LTV) + possible-duplicate
// badge (G2/G4 backend). LTV is per-currency and never FX-summed.
// ---------------------------------------------------------------------------

export function GuestStatsSection({ guest }: { guest: Guest }) {
  const ltv = guest.lifetime_values ?? [];
  const bookingsCount = guest.bookings_count ?? 0;
  const dupCount = guest.possible_duplicate_count ?? 0;
  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Overview</h2>
        {dupCount > 0 && (
          <Badge
            variant="warning"
            role="status"
            aria-label={`${dupCount} possible duplicate${dupCount === 1 ? '' : 's'} — see Merge below`}
            title="Other active guests share this email or phone — see Merge below."
          >
            {dupCount} possible duplicate{dupCount === 1 ? '' : 's'}
          </Badge>
        )}
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
        <Field label="Bookings" value={<span className="tabular-nums">{bookingsCount}</span>} />
        <div>
          <dt className="text-xs uppercase tracking-[0.06em] text-muted">Lifetime value</dt>
          <dd className="mt-0.5">
            {ltv.length === 0 ? (
              '—'
            ) : (
              <span className="tabular-nums">
                {ltv.map((v) => formatMoney(v.minor_units, v.currency)).join(' · ')}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Profile section (read + inline edit)
// ---------------------------------------------------------------------------

function ProfileSection({ guest, isTombstone = false }: { guest: Guest; isTombstone?: boolean }) {
  const [editing, setEditing] = useState(false);

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Profile</h2>
        {!editing && !isTombstone && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      {editing ? (
        <ProfileEditForm
          guest={guest}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <ProfileRead guest={guest} />
      )}
    </section>
  );
}

function ProfileRead({ guest }: { guest: Guest }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
      <Field label="Full name" value={guest.full_name} />
      <Field label="Email" value={guest.email ?? '—'} />
      <Field label="Phone" value={guest.phone ?? '—'} />
      <Field label="Language" value={guest.language ?? '—'} />
      {guest.notes && (
        <div className="md:col-span-2">
          <dt className="text-xs uppercase tracking-[0.06em] text-muted">Notes</dt>
          <dd className="mt-0.5 whitespace-pre-line">{guest.notes}</dd>
        </div>
      )}
      <div className="md:col-span-2 mt-2 text-xs text-muted">
        Created {new Date(guest.created_at).toLocaleString()}
      </div>
    </dl>
  );
}

interface ProfileFormState {
  full_name: string;
  email: string;
  phone: string;
  language: string;
  notes: string;
}

function initialProfileForm(guest: Guest): ProfileFormState {
  return {
    full_name: guest.full_name,
    email: guest.email ?? '',
    phone: guest.phone ?? '',
    language: guest.language ?? '',
    notes: guest.notes ?? '',
  };
}

function strOrNull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

function ProfileEditForm({
  guest,
  onCancel,
  onSaved,
}: {
  guest: Guest;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<ProfileFormState>(() => initialProfileForm(guest));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const full_name = form.full_name.trim();
      if (!full_name) throw new Error('Full name is required.');

      // Build body sending explicit null for emptied nullable fields so the
      // backend clears the column. @hey-ai/openapi-ts drops the null arm of
      // optional types so we cast via rawBody to preserve nulls on the wire.
      const body = rawBody({
        full_name,
        email: strOrNull(form.email),
        phone: strOrNull(form.phone),
        language: strOrNull(form.language),
        notes: strOrNull(form.notes),
      });
      return updateGuestProfile({
        throwOnError: true,
        path: { id: guest.id },
        body,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['guest', guest.id] });
      qc.invalidateQueries({ queryKey: ['guests'] });
      toast({ kind: 'success', title: 'Guest updated', message: form.full_name.trim() });
      onSaved();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError('That email is already used by another guest.');
        } else if (err.status === 400) {
          setError('Enter a valid phone number, e.g. +49 30 12345678.');
        } else {
          setError(err.message);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(String(err));
      }
    },
  });

  const set =
    <K extends keyof ProfileFormState>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [k]: e.target.value }));
    };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mut.mutate();
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label="Full name" id="guest-full-name">
          <input
            id="guest-full-name"
            type="text"
            value={form.full_name}
            onChange={set('full_name')}
            required
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Email" id="guest-email">
          <input
            id="guest-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Phone" id="guest-phone">
          <input
            id="guest-phone"
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="+49 30 12345678"
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
        <FormField label="Language (BCP-47)" id="guest-language">
          <input
            id="guest-language"
            type="text"
            value={form.language}
            onChange={set('language')}
            placeholder="en, de, ru…"
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          />
        </FormField>
      </div>

      <FormField label="Notes" id="guest-notes" className="mt-4">
        <textarea
          id="guest-notes"
          rows={3}
          value={form.notes}
          onChange={set('notes')}
          className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
        />
      </FormField>

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
          Save
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Bookings panel
// ---------------------------------------------------------------------------

function BookingsPanel({ guestId }: { guestId: string }) {
  const { data: apartments } = useQuery(apartmentsQuery);
  const { data, isLoading } = useQuery(guestBookingsQuery(guestId));

  const aptMap = new Map<string, Apartment>(
    (apartments ?? []).map((a) => [a.id, a]),
  );

  const items = data?.items ?? [];

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Bookings</h2>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && items.length === 0 && (
        <p className="text-sm text-muted">No bookings linked to this guest.</p>
      )}

      {items.length > 0 && (
        <BookingsTable items={items} aptMap={aptMap} />
      )}
    </section>
  );
}

function BookingsTable({
  items,
  aptMap,
}: {
  items: GuestBooking[];
  aptMap: Map<string, Apartment>;
}) {
  return (
    <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-control)] border border-[var(--border-hairline)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th scope="col" className="px-4 py-3">Apartment</th>
            <th scope="col" className="px-4 py-3">Check-in</th>
            <th scope="col" className="px-4 py-3">Check-out</th>
            <th scope="col" className="px-4 py-3">Status</th>
            <th scope="col" className="px-4 py-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((booking) => {
            const apt = aptMap.get(booking.apartment_id);
            const aptLabel = apt ? apt.code : booking.apartment_id.slice(0, 8);
            return (
              <tr
                key={booking.id}
                className="border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]"
              >
                <td className="px-4 py-3">
                  <Link
                    to="/bookings/$id"
                    params={{ id: booking.id }}
                    className="font-mono text-xs text-primary hover:underline"
                    title={apt ? apt.title : booking.apartment_id}
                  >
                    {aptLabel}
                  </Link>
                </td>
                <td className="px-4 py-3 tabular-nums">{booking.checkin_date}</td>
                <td className="px-4 py-3 tabular-nums">{booking.checkout_date}</td>
                <td className="px-4 py-3">
                  <BookingStatusBadge status={booking.status} />
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(booking.total_amount_minor, booking.currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conversations panel
// ---------------------------------------------------------------------------

function ConversationsPanel({ guestId }: { guestId: string }) {
  const { data, error, isLoading } = useQuery(guestConversationsQuery(guestId));

  const is401 = error instanceof ApiError && error.status === 401;
  const items = data?.items ?? [];

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Conversations</h2>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {is401 && (
        <p role="alert" className="text-sm text-destructive">
          Your session has expired.{' '}
          <Link to="/login" className="font-medium underline">
            Sign in again
          </Link>
          .
        </p>
      )}

      {!isLoading && !is401 && items.length === 0 && (
        <p className="text-sm text-muted">No conversations yet.</p>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-[var(--border-soft)] overflow-hidden rounded-[var(--radius-control)] border border-[var(--border-hairline)]">
          {items.map((thread) => (
            <ConversationRow key={thread.id} thread={thread} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ConversationRow({ thread }: { thread: ThreadSummary }) {
  return (
    <li className="flex items-center justify-between gap-4 bg-card px-4 py-3 hover:bg-[var(--surface-sunken)]">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{thread.title}</p>
          {thread.unread_count > 0 && (
            <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
              {thread.unread_count} unread
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted">
          <span className="uppercase tracking-wide">{thread.provider}</span>
          {thread.last_message_received_at && (
            <>
              <span aria-hidden="true"> · </span>
              {formatDateTime(thread.last_message_received_at)}
            </>
          )}
        </p>
      </div>
      <Link
        to="/inbox/$id"
        params={{ id: thread.id }}
        className="shrink-0 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-[var(--surface-sunken)]"
      >
        Open conversation
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Merged-from section (canonical record — lists absorbed duplicates)
// ---------------------------------------------------------------------------

function MergedFromSection({ mergedFrom }: { mergedFrom: GuestRef[] }) {
  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        Merged from
      </h2>
      <ul className="space-y-1 text-sm">
        {mergedFrom.map((g) => (
          <li key={g.id}>
            <Link
              to="/guests/$id"
              params={{ id: g.id }}
              className="text-primary hover:underline"
            >
              {g.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Merge panel
// ---------------------------------------------------------------------------

function MergePanel({ guest }: { guest: Guest }) {
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [mergeError, setMergeError] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const askConfirm = useConfirm();
  const { toast } = useToast();

  const searchQuery = guestsQuery(searchQ, 20);
  const { data: searchData, isLoading: searching } = useQuery({
    ...searchQuery,
    enabled: searchQ.length > 0,
  });

  const candidates =
    (searchData as { items: Guest[] } | undefined)?.items.filter((g) => g.id !== guest.id) ?? [];

  const mergeMut = useMutation({
    mutationFn: (targetId: string) =>
      mergeGuest({
        throwOnError: true,
        path: { id: guest.id },
        body: { into_id: targetId },
      }),
    onSuccess: (_, targetId) => {
      // Invalidate everything guest-related, then navigate to the survivor.
      qc.invalidateQueries({ queryKey: ['guests'] });
      qc.invalidateQueries({ queryKey: ['guest', guest.id] });
      qc.invalidateQueries({ queryKey: ['guest', targetId] });
      // The source's bookings just moved to the target; drop every cached
      // guest-bookings entry so the survivor's panel shows the absorbed rows.
      qc.invalidateQueries({ queryKey: ['guest-bookings'] });
      toast({
        kind: 'success',
        title: 'Guests merged',
        message: `"${guest.full_name}" was absorbed into the target.`,
      });
      void navigate({ to: '/guests/$id', params: { id: targetId } });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setMergeError(
          "Can't merge: the target has itself been merged, or this guest is already merged elsewhere.",
        );
      } else if (err instanceof ApiError) {
        setMergeError(err.message);
      } else {
        setMergeError(String(err));
      }
    },
  });

  async function handleMergeInto(target: { id: string; full_name: string }) {
    setMergeError(null);
    const ok = await askConfirm({
      title: `Merge "${guest.full_name}" into "${target.full_name}"?`,
      body: `"${guest.full_name}" will be absorbed — its bookings move to "${target.full_name}" and it disappears from lists. This cannot be undone.`,
      destructive: true,
      confirmLabel: 'Merge',
    });
    if (ok) {
      mergeMut.mutate(target.id);
    }
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearchQ(searchInput.trim());
  }

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        Merge into another guest
      </h2>
      <p className="mb-4 text-xs text-muted">
        The current guest ("{guest.full_name}") will be absorbed into the target you select —
        its bookings move to the target and it disappears from search results.
      </p>

      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <label htmlFor="merge-search" className="sr-only">
          Search for a target guest
        </label>
        <input
          id="merge-search"
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search for a guest to merge into…"
          className="flex-1 rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]"
          aria-label="Search for target guest to merge into"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {searching && <p className="mt-3 text-sm text-muted">Searching…</p>}

      {!searching && searchQ && candidates.length === 0 && (
        <p className="mt-3 text-sm text-muted">No other guests found for "{searchQ}".</p>
      )}

      {candidates.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--border-soft)] rounded-[var(--radius-control)] border border-[var(--border-hairline)] overflow-hidden">
          {candidates.map((target) => (
            <li
              key={target.id}
              className="flex items-center justify-between gap-4 px-4 py-3 bg-card hover:bg-[var(--surface-sunken)]"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{target.full_name}</p>
                {target.email && (
                  <p className="text-xs text-muted truncate">{target.email}</p>
                )}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="shrink-0"
                disabled={mergeMut.isPending}
                onClick={() => void handleMergeInto(target)}
              >
                Merge into this
              </Button>
            </li>
          ))}
        </ul>
      )}

      {mergeError && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {mergeError}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function FormField({
  label,
  id,
  className = '',
  children,
}: {
  label: string;
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
